import type { TFunction } from "i18next";
import type { IntlShape } from "react-intl";

import { convertBpToDecimal, formatNumberLocale } from "@/utils/helpers";

export type TenderKpiItem = {
  key: string;
  label: string;
  content: string;
  tooltip?: string;
};

type TenderPriceDetailsLike = {
  surcharge?: number | null;
  revenue?: number | null;
  margin?: number | null;
  yearlyRate?: number | null;
  monthlyRate?: number | null;
  pricePerHour?: number | null;
  totalEmployeeCount?: number | null;
  minutes?: number | null;
};

/**
 * Returns a standard set of KPI items to display for a tender in Calculation Overview.
 * Kept in a shared helper so multiple components can render the same KPI ordering/formatting.
 */
export function getTenderKpiItems({
  t,
  intl,
  tenderPriceDetails,
}: {
  t: TFunction;
  intl: IntlShape;
  tenderPriceDetails?: TenderPriceDetailsLike | null;
}): TenderKpiItem[] {
  if (!tenderPriceDetails) return [];

  return [
    {
      key: "margin",
      label: t("common:pricing.labels.totalmargin"),
      content: `${convertBpToDecimal(tenderPriceDetails.margin ?? 0)}%`,
    },
    {
      key: "surcharge",
      label: t("common:pricing.labels.totalsurcharge"),
      content: `${convertBpToDecimal(tenderPriceDetails.surcharge ?? 0)}%`,
      tooltip: t("edittender.calculationoverview.labels.totalsurcharge.tooltiptender"),
    },
    {
      key: "revenue",
      label: t("common:pricing.labels.revenue"),
      content: formatNumberLocale(intl, tenderPriceDetails.revenue ?? 0, "currency"),
    },
    {
      key: "yearlyRate",
      label: t("common:pricing.labels.yearlyrate"),
      content: formatNumberLocale(intl, tenderPriceDetails.yearlyRate ?? 0, "currency"),
    },
    {
      key: "monthlyRate",
      label: t("common:pricing.labels.monthlyrate"),
      content: formatNumberLocale(intl, tenderPriceDetails.monthlyRate ?? 0, "currency"),
    },
    {
      key: "pricePerHour",
      label: t("common:pricing.labels.priceperhour"),
      content: formatNumberLocale(intl, tenderPriceDetails.pricePerHour ?? 0, "currency"),
    },
    {
      key: "totalEmployeeCount",
      label: t("common:pricing.labels.totalemployeecount"),
      content: formatNumberLocale(intl, tenderPriceDetails.totalEmployeeCount ?? 0, "decimal"),
    },
    {
      key: "hours",
      label: t("common:pricing.labels.hours"),
      content: formatNumberLocale(intl, (tenderPriceDetails.minutes ?? 0) / 60, "decimal"),
    },
  ];
}
